import clsx from "clsx";
import {
  type ExtractDocsContextValue,
  getFileForSourceDocument,
  useExtractDocs,
} from "context/ExtractDocsContext";
import {
  CONTRADICTION_STATUS,
  DISCHARGE_LETTER_FORM_NAMES,
  DOCS_FORM_NAMES,
  FIELD_CREATION_TYPE,
  QUERY_PROGRESS_SUCCEED,
  RECARE_FILE_TYPE,
  SOURCE_DOCUMENT_SOURCES,
} from "core/consts";
import {
  AuctionForm,
  Contradictions,
  DocsFormDataMapped,
  DocsFormName,
  DocsFormSourceDocument,
  DocsFormStatusResponse,
  EncryptedField,
  FieldCreationType,
  FileState,
  FormMetadata,
  QueryProgress,
  RecareFileType,
  SourceFields,
} from "core/types";
import {
  Button,
  ButtonVariant,
  CheckboxGroupProps,
  ControlledCheckboxGroup,
  ControlledDatePickerProps,
  ControlledRadioGroup,
  DatePicker,
  RadioGroupProps,
  Tooltip,
} from "ds/ui";
import { useCopy } from "dsl/atoms/CopyButton/useCopy";
import { useCreateFileUrl } from "dsl/ecosystems/PatientFiles/useDownloadFile";
import {
  AlertTriangleIcon,
  CheckIcon,
  CopyIcon,
  FileSearch2Icon,
} from "lucide-react";
import { resolveArhVersion } from "onprem/resolveArhVersion";
import { Dispatch, ReactNode, SetStateAction, useCallback } from "react";
import { convertEncryptedFieldOut } from "react-forms-state";
import { useFormContext, useWatch } from "react-hook-form";
// eslint-disable-next-line no-react-hook-form-outside-ds-ui/no-react-hook-form-outside-ds-ui -- TODO: remove this once we have a way to focus the components on validation - https://recare.atlassian.net/browse/DEV-19781
import { useController } from "react-hook-form";
import { useTranslations } from "translations";
import { SourceInformation } from "./useDocsForm";

export const FORM_NAME_TO_FILE_TYPE: Record<DocsFormName, RecareFileType> = {
  [DOCS_FORM_NAMES.DISCHARGE_LETTER_CARDIOLOGY]:
    RECARE_FILE_TYPE.DISCHARGE_LETTER_CARDIOLOGY,
  [DOCS_FORM_NAMES.DISCHARGE_LETTER_ENT]: RECARE_FILE_TYPE.DISCHARGE_LETTER_ENT,
  [DOCS_FORM_NAMES.DISCHARGE_LETTER_NEUROSURGERY]:
    RECARE_FILE_TYPE.DISCHARGE_LETTER_NEUROSURGERY,
  [DOCS_FORM_NAMES.DISCHARGE_LETTER_TRAUMA_SURGERY]:
    RECARE_FILE_TYPE.DISCHARGE_LETTER_TRAUMA_SURGERY,
  [DOCS_FORM_NAMES.DRV_GENERAL]: RECARE_FILE_TYPE.DRV_GENERAL_FORM,
  [DOCS_FORM_NAMES.DRV_MEDICAL]: RECARE_FILE_TYPE.DRV_MEDICAL_FORM,
  [DOCS_FORM_NAMES.GKV_GENERAL]: RECARE_FILE_TYPE.GKV_GENERAL_FORM,
  [DOCS_FORM_NAMES.GKV_MEDICAL]: RECARE_FILE_TYPE.GKV_MEDICAL_FORM,
  [DOCS_FORM_NAMES.TRANSITIONAL_CARE]: RECARE_FILE_TYPE.TRANSITIONAL_CARE,
};

export type MigratedForms = (typeof DISCHARGE_LETTER_FORM_NAMES)[number];

export function isDischargeLetterFormName(
  formName: DocsFormName,
): formName is MigratedForms {
  return (DISCHARGE_LETTER_FORM_NAMES as readonly DocsFormName[]).includes(
    formName,
  );
}

const COPYABLE_FORMS: DocsFormName[] = [...DISCHARGE_LETTER_FORM_NAMES];

const CopyButton = <T extends DocsFormName = DocsFormName>({
  fieldName,
}: {
  fieldName: keyof DocsFormDataMapped<T>;
}) => {
  const fieldValue = useWatch({
    name: fieldName as string,
  });
  const translations = useTranslations();
  const { copyMessage, copyStatus, copyToClipboard } = useCopy({
    onCopyMessage: "todo: translation",
  });

  const renderContent = () => (
    <Button
      aria-label={translations.chat.copyMessage}
      color="primary"
      id={`copy-button-${String(fieldName)}`}
      isDisabled={!fieldValue}
      isIconOnly
      startContent={
        copyStatus === QUERY_PROGRESS_SUCCEED ? <CheckIcon /> : <CopyIcon />
      }
      trackEventProps={{
        field_name: String(fieldName),
        id: `copy-button`,
      }}
      variant="ghost"
      onPress={() => copyToClipboard(fieldValue)}
    />
  );

  if (copyStatus === QUERY_PROGRESS_SUCCEED) {
    return (
      <Tooltip content={copyMessage} isOpen placement="bottom">
        {renderContent()}
      </Tooltip>
    );
  }

  return renderContent();
};

const SourceButton = ({
  fieldName,
  isEdited,
  isIconOnly,
  isSourceFieldsConflict,
  onPress,
}: {
  fieldName: string;
  isSourceFieldsConflict: boolean;
  isEdited: boolean;
  isIconOnly: boolean;
  onPress: () => void;
}) => {
  const translations = useTranslations();
  const buttonId = `${isEdited ? "reference" : "source"}-button`;
  const buttonLabel =
    translations.extractDocs.splitView.sourceInformation.buttonSource;
  const buttonText = isEdited
    ? translations.extractDocs.splitView.sourceInformation.buttonReference
    : translations.extractDocs.splitView.sourceInformation.buttonSource;

  const sharedProps = {
    "aria-label": buttonLabel,
    className: "whitespace-nowrap",
    color: isSourceFieldsConflict ? ("danger" as const) : ("primary" as const),
    endContent: isSourceFieldsConflict ? (
      <AlertTriangleIcon />
    ) : (
      <FileSearch2Icon />
    ),
    id: `${buttonId}-${fieldName}`,
    onPress,
    trackEventProps: {
      field_name: fieldName,
      id: buttonId,
    },
    variant: isEdited ? "light" : ("ghost" as ButtonVariant),
  };

  if (isIconOnly) {
    return <Button {...sharedProps} isIconOnly />;
  }

  return <Button {...sharedProps}>{buttonText}</Button>;
};

export const SourceWrapper = <T extends DocsFormName = DocsFormName>({
  children,
  fieldName,
  formName,
  hideSource,
  isIconOnly = false,
  sourceInformation,
}: {
  children: ReactNode;
  sourceInformation: SourceInformation<T>;
  fieldName: keyof DocsFormDataMapped<T>;
  isIconOnly?: boolean;
  hideSource?: boolean;
  formName: T;
}) => {
  if (!children) {
    return null;
  }

  if (hideSource) {
    return <div className="p-1">{children}</div>;
  }

  const isSelected = sourceInformation.isSourceSelected(fieldName);
  const sourceFields = sourceInformation.getSourceFields(fieldName);
  const isEdited = sourceFields.manually_edited;

  const canCopyResult = COPYABLE_FORMS.includes(formName);
  const sourceDocuments = sourceFields.source_documents ?? [];
  const hasSourceDocuments = sourceDocuments.length > 0;
  const contradictions = sourceInformation.getContradictionFields(fieldName);

  const isSourceFieldsConflict =
    contradictions?.status === CONTRADICTION_STATUS.CONFLICT &&
    (contradictions?.candidates?.length ?? 0) > 0;

  return (
    <div
      className={clsx("flex w-full gap-2 rounded-sm px-4 tablet:py-2", {
        "bg-blue-50": isSelected,
        "bg-white": !isSelected,
      })}
    >
      <div
        className={clsx("min-w-0", {
          "basis-[70%]": !isIconOnly,
          "basis-[85%]": isIconOnly,
        })}
      >
        {children}
      </div>
      <div className="flex min-w-0 flex-1 flex-row flex-wrap items-end justify-end gap-2">
        {canCopyResult && <CopyButton fieldName={fieldName} />}
        {hasSourceDocuments ? (
          <SourceButton
            fieldName={String(fieldName)}
            isEdited={isEdited}
            isIconOnly={isIconOnly}
            isSourceFieldsConflict={isSourceFieldsConflict}
            onPress={() => sourceInformation.handleSetSelectedSource(fieldName)}
          />
        ) : null}
      </div>
    </div>
  );
};

export function getFieldCreationType(
  inferredValue: unknown,
  value: unknown,
  sourceField: SourceFields | undefined,
): FieldCreationType {
  if (inferredValue != null && value != null) {
    return FIELD_CREATION_TYPE.INFERENCE;
  }
  if (sourceField?.creation_type) {
    return sourceField.creation_type;
  }
  return FIELD_CREATION_TYPE.USER;
}

export function getManuallyEdited(
  inferredValue: unknown,
  value: unknown,
  previousFieldValue: unknown,
  sourceField: SourceFields | undefined,
): boolean {
  if (inferredValue != null) {
    return value !== inferredValue;
  }
  if (previousFieldValue != null) {
    return previousFieldValue !== value || !!sourceField?.manually_edited;
  }
  return false;
}

export function buildFormMetadata<T extends DocsFormName>({
  docsFillFormResult,
  formData,
  previousFormData,
  sourceInformation,
}: {
  formData: DocsFormDataMapped<T>;
  sourceInformation: SourceInformation<T>;
  docsFillFormResult: DocsFormStatusResponse | null;
  previousFormData: DocsFormDataMapped<T> | null | undefined;
}): FormMetadata<T> {
  const sourceFields = Object.entries(formData).reduce(
    (acc, [key, value]) => {
      const inferredValue = docsFillFormResult?.form[key]?.value;
      const sourceField = sourceInformation.getSourceFields(
        key as keyof DocsFormDataMapped<T>,
      );
      const sourceDocuments = sourceField?.source_documents ?? [];
      const previousFieldValue =
        previousFormData?.[key as keyof DocsFormDataMapped<T>];

      return {
        ...acc,
        [key]: {
          creation_type: getFieldCreationType(
            inferredValue,
            value,
            sourceField,
          ),
          manually_edited: getManuallyEdited(
            inferredValue,
            value,
            previousFieldValue,
            sourceField,
          ),
          source_documents: sourceDocuments,
        },
      };
    },
    {} as FormMetadata<T>["source_fields"],
  );

  // Contradictions are stored at form level (encrypted), not per field in source_fields.
  const contradictions = Object.entries(formData).reduce<
    Record<string, Contradictions>
  >((acc, [key]) => {
    const fieldContradictions = sourceInformation.getContradictionFields(
      key as keyof DocsFormDataMapped<T>,
    );

    if (fieldContradictions == null) return acc;
    const value =
      typeof fieldContradictions === "object" &&
      "decrypted" in fieldContradictions
        ? (fieldContradictions as { decrypted?: string }).decrypted
        : fieldContradictions;
    const parsed: Contradictions | null =
      typeof value === "object" && value !== null && "status" in value
        ? (value as unknown as Contradictions)
        : typeof value === "string"
        ? (() => {
            try {
              return JSON.parse(value) as Contradictions;
            } catch {
              return null;
            }
          })()
        : null;
    if (parsed?.status != null) acc[key] = parsed;
    return acc;
  }, {});

  return {
    contradictions: (convertEncryptedFieldOut(JSON.stringify(contradictions)) ??
      null) as EncryptedField | null,
    source_fields: sourceFields,
  };
}

export function buildSaveFormMetadata<T extends DocsFormName>({
  auctionForms,
  docsFillFormResult,
  formData,
  formName,
  includeAuctionSchemaVersion = false,
  previousFormData,
  sourceInformation,
}: {
  auctionForms?: AuctionForm[] | null;
  docsFillFormResult: DocsFormStatusResponse | null;
  formData: DocsFormDataMapped<T>;
  formName: T;
  includeAuctionSchemaVersion?: boolean;
  previousFormData: DocsFormDataMapped<T> | null | undefined;
  sourceInformation: SourceInformation<T>;
}): FormMetadata<T> {
  const metadata: FormMetadata<T> = {
    ...buildFormMetadata({
      docsFillFormResult,
      formData,
      previousFormData,
      sourceInformation,
    }),
    // Any form edit done not in the doctor approval page resets the approval status.
    is_doctor_approved: false,
  };

  const isGkvForm =
    formName === DOCS_FORM_NAMES.GKV_GENERAL ||
    formName === DOCS_FORM_NAMES.GKV_MEDICAL;

  if (!includeAuctionSchemaVersion || !isGkvForm) {
    return metadata;
  }

  return {
    ...metadata,
    schema_version: resolveArhVersion(auctionForms),
  };
}

function buildSelectedSourceDocument(
  sourceDocument: DocsFormSourceDocument,
  patch: {
    filename?: string;
    uploadedAt: number | undefined;
    url: string;
    is_deleted?: boolean;
  },
): DocsFormSourceDocument {
  return {
    ...sourceDocument,
    filename: patch.filename ?? sourceDocument.filename,
    is_deleted: patch.is_deleted ?? sourceDocument.is_deleted,
    uploaded_at: patch.uploadedAt,
    url: patch.url,
  };
}

type SourceDocumentFileListEntry = { name: string; file: FileState };

async function buildSelectedSourceDocumentForFileEntry(
  sourceDocument: DocsFormSourceDocument,
  entry: SourceDocumentFileListEntry | undefined,
  urlCache: Map<string, string>,
  canUseExtractedDocument: boolean | undefined,
  utils: ExtractDocsContextValue["utils"],
  createFileUrl: (file: FileState) => Promise<string | undefined>,
): Promise<DocsFormSourceDocument | undefined> {
  if (!entry) {
    return buildSelectedSourceDocument(sourceDocument, {
      is_deleted: true,
      uploadedAt: undefined,
      url: "",
    });
  }

  const fromEntry = (url: string) =>
    buildSelectedSourceDocument(sourceDocument, {
      filename: entry.name,
      uploadedAt: entry.file.updated_at!,
      url,
    });

  const cacheKey = String(sourceDocument.id);
  const cachedUrl = urlCache.get(cacheKey);
  if (cachedUrl) {
    return fromEntry(cachedUrl);
  }

  if (canUseExtractedDocument) {
    const extractedDocument = utils.getExtractedDocument(entry.file);
    if (extractedDocument?.source_document_uri) {
      return fromEntry(extractedDocument.source_document_uri);
    }
  }

  const url = await createFileUrl(entry.file);
  if (url) {
    urlCache.set(cacheKey, url);
    return fromEntry(url);
  }

  return undefined;
}

export function useSelectSourceDocumentFile({
  canUseExtractedDocument,
  externalId,
  onComplete,
  setProgress,
}: {
  externalId: string;
  onComplete?: () => void;
  setProgress?: Dispatch<SetStateAction<QueryProgress>>;
  canUseExtractedDocument?: boolean;
}) {
  const { utils } = useExtractDocs();
  const createFileUrl = useCreateFileUrl({
    externalId,
    onComplete,
    setProgress,
  });

  return useCallback(
    async ({
      files,
      sourceDocument,
      urlCache,
    }: {
      sourceDocument: DocsFormSourceDocument;
      files: { name: string; file: FileState }[];
      urlCache: Map<string, string>;
    }): Promise<DocsFormSourceDocument | undefined> => {
      if (sourceDocument.source === SOURCE_DOCUMENT_SOURCES.KIS_DATA) {
        const extractedDocument = utils.getExtractedDocument({
          id: sourceDocument.id,
        } as FileState);

        if (extractedDocument?.source_document_uri) {
          return buildSelectedSourceDocument(sourceDocument, {
            filename: extractedDocument.filename,
            uploadedAt: undefined,
            url: extractedDocument.source_document_uri,
          });
        }

        return buildSelectedSourceDocument(sourceDocument, {
          is_deleted: true,
          uploadedAt: undefined,
          url: "",
        });
      }

      if (sourceDocument.source === SOURCE_DOCUMENT_SOURCES.RECARE_DATA) {
        return buildSelectedSourceDocument(sourceDocument, {
          uploadedAt: undefined,
          url: "",
        });
      }

      if (
        sourceDocument.source === SOURCE_DOCUMENT_SOURCES.RECARE_FILE ||
        sourceDocument.source === SOURCE_DOCUMENT_SOURCES.KIS_FILE
      ) {
        const entry = getFileForSourceDocument(files, sourceDocument);
        return buildSelectedSourceDocumentForFileEntry(
          sourceDocument,
          entry,
          urlCache,
          canUseExtractedDocument,
          utils,
          createFileUrl,
        );
      }

      return undefined;
    },
    [canUseExtractedDocument, createFileUrl, utils],
  );
}

// TODO: remove these helper functions once we have a way to focus the components on validation
// Adds `tabIndex={-1}` and `div wth ref` so RHF `shouldFocusError` can focus non-native group roots.
// https://recare.atlassian.net/browse/DEV-19781
export function ControlledCheckboxGroupWithFocus<T = string | number>(
  props: Omit<CheckboxGroupProps<T>, "onValueChange" | "errorMessage"> & {
    name: string;
  },
) {
  return <ControlledCheckboxGroup {...props} tabIndex={-1} />;
}

export function ControlledRadioGroupWithFocus<T = string | number>(
  props: Omit<RadioGroupProps<T>, "onValueChange" | "errorMessage"> & {
    name: string;
  },
) {
  return <ControlledRadioGroup {...props} tabIndex={-1} />;
}

export function ControlledDatePickerWithFocus({
  name,
  ...props
}: ControlledDatePickerProps) {
  const { control } = useFormContext();
  const { field, fieldState } = useController({ control, name });
  const { ref, ...fieldRest } = field;

  return (
    <div ref={ref} className="outline-none focus:outline-none" tabIndex={-1}>
      <DatePicker
        {...props}
        {...fieldRest}
        {...fieldState}
        errorMessage={fieldState?.error?.message}
        isInvalid={!!fieldState?.error?.message}
        name={name}
        value={field.value}
        onChange={(value) => {
          field.onChange(value);
        }}
      />
    </div>
  );
}
